// client/src/components/world/SummaryMesh.jsx
// Итерация 9.6 (восстановление): рендер summary_mesh структуры.
//
// Что делает:
//   - Принимает summaryMesh (объект от useSummaryMesh).
//   - Выбирает LOD по distance (0 = full, 1..3 = упрощённый).
//   - Рендерит parts[] через MeshPart (8 примитивов от mesh/geometry.ex).
//   - Оборачивает в SceneErrorBoundary.
//
// ⚠️ Работает ВНУТРИ <Canvas> (React Three Fiber).
//    Не знает про Redux, hooks, состояние. Только props.
//
// Контракт:
//   <SummaryMesh
//     summaryMesh={mesh}        // от useSummaryMesh
//     distance={distance}       // расстояние до камеры (для LOD)
//     fallback={null}           // что рендерить, если нет данных
//   />

import React, { useMemo } from 'react';
import SceneErrorBoundary from './SceneErrorBoundary';

// ============================================================================
// LOD-ПОРОГИ (по distance, как в mesh/lod.ex)
// ============================================================================
const LOD_THRESHOLDS = [
  { maxDistance: 200, level: 0 },   // full
  { maxDistance: 500, level: 1 },   // упрощённый
  { maxDistance: 1000, level: 2 },  // ещё проще
  { maxDistance: Infinity, level: 3 }, // минимальный
];

function selectLodLevel(distance) {
  const d = Number(distance) || 0;
  for (const { maxDistance, level } of LOD_THRESHOLDS) {
    if (d <= maxDistance) return level;
  }
  return 3;
}

// ============================================================================
// ИЗВЛЕЧЕНИЕ PARTS ИЗ summaryMesh
// ============================================================================
function extractParts(summaryMesh, lodLevel) {
  if (!summaryMesh) return [];

  // Вариант A: LOD-уровни { lod_0: [...], lod_1: [...], ... }
  const lodKey = `lod_${lodLevel}`;
  if (Array.isArray(summaryMesh[lodKey])) {
    return summaryMesh[lodKey];
  }

  // Fallback: если нет нужного LOD — ищем ближайший с данными
  for (let l = lodLevel; l >= 0; l--) {
    if (Array.isArray(summaryMesh[`lod_${l}`])) {
      return summaryMesh[`lod_${l}`];
    }
  }
  for (let l = lodLevel + 1; l <= 3; l++) {
    if (Array.isArray(summaryMesh[`lod_${l}`])) {
      return summaryMesh[`lod_${l}`];
    }
  }

  // Вариант B: плоский список { parts: [...] }
  if (Array.isArray(summaryMesh.parts)) {
    return summaryMesh.parts;
  }

  // Fallback: если summaryMesh сам — массив
  if (Array.isArray(summaryMesh)) {
    return summaryMesh;
  }

  return [];
}

// ============================================================================
// ВЕКТОР → [x, y, z]
// ============================================================================
function toVec3(v, defaultValue = [0, 0, 0]) {
  if (Array.isArray(v) && v.length >= 3) {
    return [Number(v[0]) || 0, Number(v[1]) || 0, Number(v[2]) || 0];
  }
  if (v && typeof v === 'object') {
    return [Number(v.x) || 0, Number(v.y) || 0, Number(v.z) || 0];
  }
  if (typeof v === 'number') {
    return [v, v, v];
  }
  return defaultValue;
}

// ============================================================================
// ЦВЕТ
// ============================================================================
function toColor(c, defaultValue = '#888888') {
  if (!c) return defaultValue;
  if (typeof c === 'string') return c;
  if (typeof c === 'object') {
    const { r = 1, g = 1, b = 1 } = c;
    const toHex = (n) =>
      Math.max(0, Math.min(255, Math.round(Number(n) * 255)))
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  return defaultValue;
}

// ============================================================================
// MATERIAL → R3F props
// ============================================================================
function materialToProps(material, color) {
  const base = { color };

  if (!material || typeof material !== 'string') {
    return { ...base, roughness: 0.6, metalness: 0.4 };
  }

  switch (material) {
    case 'metal':
      return { ...base, roughness: 0.3, metalness: 0.9 };
    case 'glass':
      return { ...base, roughness: 0.1, metalness: 0.1, transparent: true, opacity: 0.6 };
    case 'energy':
      return { ...base, emissive: color, emissiveIntensity: 0.8, roughness: 0.2, metalness: 0.1 };
    case 'concrete':
    case 'stone':
      return { ...base, roughness: 0.9, metalness: 0.05 };
    case 'plastic':
      return { ...base, roughness: 0.7, metalness: 0.1 };
    case 'fabric':
      return { ...base, roughness: 1.0, metalness: 0.0 };
    default:
      return { ...base, roughness: 0.6, metalness: 0.4 };
  }
}

// ============================================================================
// MESH PART — один примитив
// ============================================================================
function MeshPart({ part, index }) {
  const position = toVec3(part.position, [0, 0, 0]);
  const rotation = toVec3(part.rotation, [0, 0, 0]);
  const scale = toVec3(part.scale, [1, 1, 1]);
  const color = toColor(part.color, part.material === 'energy' ? '#00e5ff' : '#888888');
  const materialProps = materialToProps(part.material, color);

  // Размеры — часть может задавать их через `size`, `dimensions`, `radius`, `height`, `width`, `depth`
  const size = part.size;
  const dims = part.dimensions || {};

  const shape = String(part.shape || part.primitive || part.type || 'box').toLowerCase();

  const geometry = useMemo(() => {
    switch (shape) {
      case 'box': {
        const width = Number(part.width ?? dims.width ?? (Array.isArray(size) ? size[0] : size?.x) ?? 1);
        const height = Number(part.height ?? dims.height ?? (Array.isArray(size) ? size[1] : size?.y) ?? 1);
        const depth = Number(part.depth ?? dims.depth ?? (Array.isArray(size) ? size[2] : size?.z) ?? 1);
        return <boxGeometry args={[width, height, depth]} />;
      }
      case 'cylinder': {
        const radiusTop = Number(part.radius_top ?? part.radiusTop ?? dims.radius ?? part.radius ?? 0.5);
        const radiusBottom = Number(part.radius_bottom ?? part.radiusBottom ?? dims.radius ?? part.radius ?? 0.5);
        const height = Number(part.height ?? dims.height ?? 1);
        return <cylinderGeometry args={[radiusTop, radiusBottom, height, 24]} />;
      }
      case 'sphere': {
        const radius = Number(part.radius ?? dims.radius ?? (Array.isArray(size) ? size[0] : 0.5));
        return <sphereGeometry args={[radius, 24, 16]} />;
      }
      case 'cone': {
        const radius = Number(part.radius ?? dims.radius ?? 0.5);
        const height = Number(part.height ?? dims.height ?? 1);
        return <coneGeometry args={[radius, height, 24]} />;
      }
      case 'torus': {
        const radius = Number(part.radius ?? dims.radius ?? 0.5);
        const tube = Number(part.tube ?? dims.tube ?? 0.15);
        return <torusGeometry args={[radius, tube, 16, 32]} />;
      }
      case 'capsule': {
        const radius = Number(part.radius ?? dims.radius ?? 0.4);
        const length = Number(part.length ?? dims.length ?? 1);
        return <capsuleGeometry args={[radius, length, 8, 16]} />;
      }
      case 'plane': {
        const width = Number(part.width ?? dims.width ?? (Array.isArray(size) ? size[0] : 1));
        const height = Number(part.height ?? dims.height ?? (Array.isArray(size) ? size[1] : 1));
        return <planeGeometry args={[width, height]} />;
      }
      case 'pyramid': {
        const radius = Number(part.radius ?? dims.radius ?? 0.5);
        const height = Number(part.height ?? dims.height ?? 1);
        return <coneGeometry args={[radius, height, 4]} />;
      }
      default: {
        // Fallback: box 1×1×1
        return <boxGeometry args={[1, 1, 1]} />;
      }
    }
  }, [shape, part, dims, size]);

  const key = part.id || part.instance_id || `part-${index}`;

  return (
    <mesh
      key={key}
      position={position}
      rotation={rotation}
      scale={scale}
      castShadow
      receiveShadow
    >
      {geometry}
      <meshStandardMaterial {...materialProps} />
    </mesh>
  );
}

// ============================================================================
// ПУБЛИЧНЫЙ КОМПОНЕНТ
// ============================================================================
export default function SummaryMesh({
  summaryMesh,
  distance = 0,
  fallback = null,
}) {
  const lodLevel = useMemo(() => selectLodLevel(distance), [distance]);
  const parts = useMemo(() => extractParts(summaryMesh, lodLevel), [summaryMesh, lodLevel]);

  if (!summaryMesh || parts.length === 0) {
    return fallback;
  }

  return (
    <SceneErrorBoundary fallback={fallback} label="SummaryMesh">
      <group name="summary-mesh">
        {parts.map((part, idx) => (
          <MeshPart key={part.id || part.instance_id || `p-${idx}`} part={part} index={idx} />
        ))}
      </group>
    </SceneErrorBoundary>
  );
}

// ============================================================================
// ЭКСПОРТЫ (для тестов и переиспользования)
// ============================================================================
export { MeshPart, selectLodLevel, extractParts, toVec3, toColor, materialToProps };